import {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { AliasNavigationContext } from './AliasNavigationContext';
import { MaybeNil } from '../../../utils/interfaces';
import { scrollToAliasIdAndHighlight } from './AliasHighlight';
import { GraphQLUserAliasId } from '../../../utils/types';

interface IUseAliasNavigationSourceReturn {
  highlightRef: MutableRefObject<HTMLDivElement | null>;
}

/**
 * Hook to register aliasable entity and to remove it once unmounted
 *
 * @param aliasId
 */
export const useAliasNavigationSource = (
  aliasId: MaybeNil<GraphQLUserAliasId>,
): IUseAliasNavigationSourceReturn => {
  const context = useContext(AliasNavigationContext);
  const highlightRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (context) {
      context.addAliasedEntry(aliasId, highlightRef);
    }

    return () => {
      if (context) {
        context.removeAliasedEntry(aliasId);
      }
    };
    // addAliasedEntry/removeAliasedEntry modify the context and if included in the effect dependencies
    // cause infinite loop. We need to hook on aliasId change
    // eslint-disable-next-line
  }, [aliasId]);

  return {
    highlightRef,
  };
};

interface IUseAliasNavigationReturn {
  highlight: (aliasId: GraphQLUserAliasId) => void;
  currentAliasAnchors: Array<GraphQLUserAliasId>;
}

/**
 * Hook that exposes currently registered alias anchors on the page and a function to "highlight" them
 */
export const useAliasNavigation = (): IUseAliasNavigationReturn => {
  const context = useContext(AliasNavigationContext);

  const highlight: IUseAliasNavigationReturn['highlight'] = useCallback(
    (aliasId) => {
      const element = context?.aliasedEntries[aliasId]?.current;
      if (element) {
        scrollToAliasIdAndHighlight(element);
      }
    },
    [context],
  );

  return {
    highlight,
    currentAliasAnchors: Object.keys(context?.aliasedEntries ?? {}),
  };
};
